import { useState, useEffect } from 'react'
import {
  Box, Card, CardContent, Typography, Divider, Switch, FormControlLabel,
  Button, Alert, CircularProgress, Stack,
} from '@mui/material'
import { useSelector } from 'react-redux'
import { RootState } from '../store'
import { tenantApi } from '../services/api'
import TenantFeaturesPanel from '../components/TenantFeaturesPanel'
import LicenceStatusPanel from '../components/LicenceStatusPanel'

const SettingsPage = () => {
  const { user } = useSelector((state: RootState) => state.auth)
  const tenantId = user?.tenant_id || ''
  // Manager-or-higher can toggle. Backend enforces; frontend gating just
  // hides the affordance from viewers (defense-in-depth, matches RBAC-5).
  const canToggleSemanticIntent =
    !!user && ['analyst', 'manager', 'admin'].includes(user.role)

  const [semanticIntentEnabled, setSemanticIntentEnabled] = useState<boolean | null>(null)
  const [pendingSemanticIntent, setPendingSemanticIntent] = useState<boolean | null>(null)
  const [loadingSemantic, setLoadingSemantic] = useState(true)
  const [savingSemantic, setSavingSemantic] = useState(false)
  const [semanticError, setSemanticError] = useState<string | null>(null)
  const [semanticUpdatedAt, setSemanticUpdatedAt] = useState<string | null>(null)

  useEffect(() => {
    if (!tenantId) return
    setLoadingSemantic(true)
    tenantApi
      .getSemanticIntent(tenantId)
      .then((resp) => {
        const enabled = Boolean(resp.data?.semantic_intent_enabled)
        setSemanticIntentEnabled(enabled)
        setPendingSemanticIntent(enabled)
        setSemanticUpdatedAt(resp.data?.updated_at ?? null)
      })
      .catch((err) => {
        const code = err?.response?.status
        if (code === 403) {
          setSemanticError('You do not have access to this setting.')
        } else if (code === 404) {
          setSemanticError('Setting unavailable for this tenant.')
        } else {
          setSemanticError(err?.message ?? 'Failed to load setting')
        }
      })
      .finally(() => setLoadingSemantic(false))
  }, [tenantId])

  const handleSaveSemantic = async () => {
    if (pendingSemanticIntent === null || pendingSemanticIntent === semanticIntentEnabled) return
    setSavingSemantic(true)
    setSemanticError(null)
    try {
      const resp = await tenantApi.setSemanticIntent(tenantId, pendingSemanticIntent)
      setSemanticIntentEnabled(Boolean(resp.data?.semantic_intent_enabled))
      setSemanticUpdatedAt(resp.data?.updated_at ?? null)
    } catch (err: any) {
      const code = err?.response?.status
      if (code === 403) {
        setSemanticError('Only managers and admins can change this setting.')
      } else {
        setSemanticError(err?.message ?? 'Failed to save')
      }
      // revert pending state
      setPendingSemanticIntent(semanticIntentEnabled)
    } finally {
      setSavingSemantic(false)
    }
  }

  const semanticChanged =
    pendingSemanticIntent !== null && pendingSemanticIntent !== semanticIntentEnabled

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>Settings</Typography>

      {/* INT-5 — Intent Detection toggle.
          NOTE: The final layman-friendly explanatory copy is deferred —
          see INT-5 task description for the drafted verbatim. Using a
          short placeholder for now so the wiring ships first. */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Intent Detection</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enable semantic analysis of each agent's declared goal so ARIAS can
            distinguish a rephrase (e.g. "be helpful" → "be very helpful") from
            a real change in the agent's purpose. When off, ARIAS uses a hash
            of the goal text and any change — including a typo fix — triggers
            a drift event. Full explanation forthcoming.
          </Typography>

          {loadingSemantic ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={20} />
            </Box>
          ) : (
            <>
              {semanticError && (
                <Alert severity="warning" sx={{ mb: 2 }}>{semanticError}</Alert>
              )}

              <Stack direction="row" alignItems="center" spacing={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!pendingSemanticIntent}
                      onChange={(e) => setPendingSemanticIntent(e.target.checked)}
                      disabled={!canToggleSemanticIntent || savingSemantic}
                    />
                  }
                  label="Enable semantic intent detection"
                />
                {semanticChanged && (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleSaveSemantic}
                    disabled={savingSemantic}
                  >
                    {savingSemantic ? 'Saving…' : 'Save'}
                  </Button>
                )}
                {semanticChanged && (
                  <Button
                    size="small"
                    onClick={() => setPendingSemanticIntent(semanticIntentEnabled)}
                    disabled={savingSemantic}
                  >
                    Cancel
                  </Button>
                )}
              </Stack>

              {!canToggleSemanticIntent && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Only analysts, managers, or admins can change this setting.
                </Typography>
              )}

              {semanticUpdatedAt && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Last updated: {new Date(semanticUpdatedAt).toLocaleString()}
                </Typography>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* About */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>About ARIAS</Typography>
          <Typography variant="body2" color="text.secondary">
            ARIAS — The control plane for AI agents. Scans your agent
            codebase, classifies architecture patterns, scores maturity across six
            dimensions, and catches behavioral drift before it reaches production.
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" color="text.secondary">
            Version 1.0.2 | 9 Microservices | 35+ Frameworks | 6-Dimension Maturity Scoring
          </Typography>
        </CardContent>
      </Card>

      <LicenceStatusPanel />
      <TenantFeaturesPanel tenantId={tenantId} />
    </Box>
  )
}

export default SettingsPage
